import { useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Section } from '@/components/fleetguardian';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubAlertTrend } from '@/api/hooks/useFraudHubAlertTrend';
import {
  chartAxis,
  chartGrid,
  chartLegend,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
  chartTooltipStyle,
} from '@/lib/chartTheme';
import { SEVERITY_FILL } from '@/lib/severity';
import { parsePeriod } from '@/lib/timePeriod';

// Critical → Watch, stacked Critical-at-bottom (first Area renders at the
// baseline). 'Normal'/'total' are omitted (production alerts never emit Normal;
// total is a convenience series). Keys are the lowercase wire severities.
const STACK: { key: string; name: string; fill: string }[] = [
  { key: 'critical', name: 'Critical', fill: SEVERITY_FILL.Critical },
  { key: 'high', name: 'High', fill: SEVERITY_FILL.High },
  { key: 'medium', name: 'Medium', fill: SEVERITY_FILL.Medium },
  { key: 'low', name: 'Low', fill: SEVERITY_FILL.Low },
  { key: 'watch', name: 'Watch', fill: SEVERITY_FILL.Watch },
];

function tickFormatter(bucketSize: string) {
  return (iso: string): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return iso;
    if (bucketSize === 'month') {
      return d.toLocaleDateString('en-ZA', {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      });
    }
    return d.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  };
}

/**
 * Fraud dashboard alert-trend (Wave 2C) — a stacked-by-severity AREA chart from
 * the NEW `/fraud-hub/alert-trend` endpoint (analytics.fraud_alert_daily_by_dim).
 * Buckets day/week/month per the resolved period; all aggregation is
 * server-side (the SPA plots the pre-bucketed series verbatim).
 */
export function AlertTrendChart(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubAlertTrend(period);

  const label = data?.period_label ? ` — ${data.period_label}` : '';
  const fmt = tickFormatter(data?.bucket_size ?? 'day');

  const hasData =
    !!data &&
    data.buckets.some((b) =>
      STACK.some((s) => typeof b[s.key] === 'number' && (b[s.key] as number) > 0),
    );

  return (
    <Section title={`Alert Trend by Severity${label}`}>
      {isLoading && <Skeleton className="h-[280px] w-full" />}
      {!isLoading && isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load alert trend: {error?.message ?? 'unknown error'}
        </div>
      )}
      {!isLoading && !isError && !hasData && (
        <div className="flex h-[280px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <p className="font-medium">No alerts in this window</p>
          <p className="text-xs">The chart fills in once alerts land in the period.</p>
        </div>
      )}
      {!isLoading && !isError && hasData && (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.buckets} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="bucket" {...chartAxis} tickFormatter={fmt} minTickGap={24} />
              <YAxis {...chartAxis} allowDecimals={false} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                itemStyle={chartTooltipItemStyle}
                labelStyle={chartTooltipLabelStyle}
                labelFormatter={(l: string) => fmt(l)}
              />
              <Legend {...chartLegend} />
              {STACK.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stackId="severity"
                  stroke={s.fill}
                  fill={s.fill}
                  fillOpacity={0.75}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
}

export default AlertTrendChart;
